import { Controller } from "@hotwired/stimulus"

// Reading behaviour on an article page: progress bar, scroll-depth milestones,
// read-completion, CTA and internal-link clicks.
//
// One scroll listener for the whole page, rAF-throttled. The progress bar and
// the depth milestones are the same measurement, so measuring twice would be
// two listeners fighting over the same layout read.
//
// Everything reports through ahoy.track, which means each event is already
// attached to a visit that Ahoy::Store has stamped with partner_id. Per-domain
// blog reporting therefore needs no extra plumbing here.
export default class extends Controller {
  static targets = ["progress", "cta", "internalLink", "tocLink"]
  static values = {
    postId: Number,
    slug: String,
    enabled: Boolean
  }

  connect() {
    this.depthsSent = new Set()
    this.readSent = false
    this.startedAt = Date.now()
    this.ticking = false

    this.onScroll = this.onScroll.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })

    this.bindClicks()
    this.onScroll()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
  }

  onScroll() {
    if (this.ticking) return
    this.ticking = true
    window.requestAnimationFrame(() => {
      this.measure()
      this.ticking = false
    })
  }

  measure() {
    const doc = document.documentElement
    const scrollable = doc.scrollHeight - window.innerHeight

    // A short article that does not scroll is 100% read the moment it renders.
    // Without this guard the division below is Infinity and no milestone ever
    // fires for exactly the articles most likely to be read in full.
    const pct = scrollable <= 0
      ? 100
      : Math.min(100, Math.round((window.scrollY / scrollable) * 100))

    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${pct}%`
    }

    for (const milestone of [25, 50, 75, 100]) {
      if (pct >= milestone && !this.depthsSent.has(milestone)) {
        this.depthsSent.add(milestone)
        this.track("Blog scroll depth", { pct: milestone })
      }
    }

    this.maybeReadComplete(pct)
  }

  // "Read" needs both signals. Depth alone counts a bot or a reader who hits
  // End; dwell alone counts an abandoned tab. Requiring 100% AND 30 seconds
  // makes the read-through rate mean something.
  maybeReadComplete(pct) {
    if (this.readSent || pct < 100) return

    const seconds = Math.round((Date.now() - this.startedAt) / 1000)
    if (seconds < 30) return

    this.readSent = true
    this.track("Blog read complete", { seconds })
  }

  bindClicks() {
    this.ctaTargets.forEach((cta) => {
      cta.addEventListener("click", () => {
        this.track("Blog CTA click", {
          section_id: Number(cta.dataset.ctaSectionId) || null,
          target_type: cta.dataset.ctaTargetType || null,
          target_id: Number(cta.dataset.ctaTargetId) || null
        })
      })
    })

    this.internalLinkTargets.forEach((link) => {
      link.addEventListener("click", () => {
        this.track("Blog internal link click", {
          href: link.getAttribute("href"),
          link_type: this.linkType(link.getAttribute("href"))
        })
      })
    })
  }

  linkType(href) {
    if (!href) return "other"
    if (href.includes("/anlass/")) return "occasion"
    if (href.includes("/ratgeber/")) return "post"
    if (href.includes("-gutschein")) return "city"
    return "other"
  }

  track(event, properties) {
    // enabledValue mirrors the server-side `Rails.env.production?` guard, so
    // staging QA does not pollute the reporting tables.
    if (!this.enabledValue) return
    if (typeof window.ahoy === "undefined") return

    try {
      window.ahoy.track(event, {
        post_id: this.postIdValue,
        slug: this.slugValue,
        ...properties
      })
    } catch (e) {
      // Analytics must never break the page it is measuring.
    }
  }
}
